import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import { mockJobs, jobCategories, contractTypes, type Job } from '@/mocks/jobs';
import { mapDbJob, formatSalary } from '@/lib/jobs';

const jobsHero =
  'https://readdy.ai/api/search-image?query=African%20professionals%20collaborating%20in%20a%20bright%20modern%20office%20space%2C%20diverse%20team%20in%20business%20attire%2C%20career%20opportunities%20and%20growth%20atmosphere%2C%20warm%20natural%20lighting%2C%20clean%20contemporary%20corporate%20interior%2C%20editorial%20photography&width=1600&height=800&seq=jobs-hero-dyn-01&orientation=landscape';

type SortKey = 'recent' | 'salary-desc' | 'salary-asc';

function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>(mockJobs);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [contract, setContract] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('jobs')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (err) throw err;
      if (data && data.length > 0) {
        setJobs((data as unknown[]).map((row) => mapDbJob(row as never)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les offres.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = jobs.filter((job) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      job.title.toLowerCase().includes(q) ||
      job.company.toLowerCase().includes(q) ||
      job.description.toLowerCase().includes(q);
    const matchesCategory = !category || job.category === category;
    const matchesContract = !contract || job.contractType === contract;
    const matchesCity = !city || job.city === city;

    const minVal = salaryMin ? Number(salaryMin) : null;
    const maxVal = salaryMax ? Number(salaryMax) : null;
    const hasSalary = job.salaryMin !== null || job.salaryMax !== null;
    const jobLow = job.salaryMin ?? job.salaryMax ?? 0;
    const jobHigh = job.salaryMax ?? job.salaryMin ?? 0;
    const matchesSalary =
      (!minVal && !maxVal) ||
      (hasSalary &&
        (!minVal || jobHigh >= minVal) &&
        (!maxVal || jobLow <= maxVal));

    return matchesSearch && matchesCategory && matchesContract && matchesCity && matchesSalary;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'salary-desc') {
      const am = a.salaryMax ?? a.salaryMin ?? 0;
      const bm = b.salaryMax ?? b.salaryMin ?? 0;
      return bm - am;
    }
    if (sort === 'salary-asc') {
      const am = a.salaryMin ?? a.salaryMax ?? 0;
      const bm = b.salaryMin ?? b.salaryMax ?? 0;
      return am - bm;
    }
    return b.postedAt.localeCompare(a.postedAt);
  });

  const cities = Array.from(new Set(jobs.map((j) => j.city)));

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={jobsHero}
              alt="WorldApeiron Jobs - offres d'emploi et recrutement"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-4">
                WorldApeiron Jobs
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
                Trouvez l&apos;emploi qui vous correspond
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-8 leading-relaxed">
                Découvrez les offres d&apos;emploi publiées par les entreprises du Togo et d&apos;Afrique.
                Postulez en ligne avec votre CV et votre lettre de motivation.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 max-w-xl flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-secondary-400">
                  <div className="w-11 h-11 flex items-center justify-center shrink-0">
                    <i className="ri-search-line text-foreground-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un poste, une entreprise..."
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 pr-4 outline-none"
                  />
                </div>
                <Link
                  to="/dashboard/jobs/new"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary-500 text-foreground-950 font-semibold rounded-full hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  Publier une offre
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="jobs" />
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <span className="text-sm font-semibold text-foreground-400">
              {filtered.length} offre{filtered.length > 1 ? 's' : ''} d&apos;emploi
            </span>
            <div className="flex items-center gap-2">
              <label htmlFor="jobs-sort" className="text-sm text-foreground-400">Trier par</label>
              <select
                id="jobs-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="bg-background-100 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-secondary-400"
              >
                <option value="recent">Plus récentes</option>
                <option value="salary-desc">Salaire décroissant</option>
                <option value="salary-asc">Salaire croissant</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  category === null
                    ? 'bg-secondary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-secondary-400/40'
                }`}
              >
                Tous secteurs
              </button>
              {jobCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    category === cat
                      ? 'bg-secondary-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-secondary-400/40'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setContract(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  contract === null
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-foreground-300/60'
                }`}
              >
                Tous contrats
              </button>
              {contractTypes.map((ct) => (
                <button
                  key={ct}
                  onClick={() => setContract(ct)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    contract === ct
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-foreground-300/60'
                  }`}
                >
                  {ct}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCity(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  city === null
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                }`}
              >
                <i className="ri-map-pin-line mr-1" />
                Toutes les villes
              </button>
              {cities.map((c) => (
                <button
                  key={c}
                  onClick={() => setCity(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    city === c
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-foreground-400 flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-money-dollar-circle-line" />
                Salaire
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                  placeholder="Min"
                  className="w-28 bg-background-100 border border-background-300/60 rounded-md px-3 py-1.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
                />
                <span className="text-foreground-400">—</span>
                <input
                  type="number"
                  min="0"
                  value={salaryMax}
                  onChange={(e) => setSalaryMax(e.target.value)}
                  placeholder="Max"
                  className="w-28 bg-background-100 border border-background-300/60 rounded-md px-3 py-1.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
                />
                <span className="text-xs text-foreground-400 whitespace-nowrap">FCFA / mois</span>
              </div>
              {(salaryMin || salaryMax) && (
                <button
                  onClick={() => {
                    setSalaryMin('');
                    setSalaryMax('');
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-accent-600 bg-accent-500/10 hover:bg-accent-500/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-close-line" />
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Jobs list */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
                <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
                <i className="ri-briefcase-line text-3xl text-secondary-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Aucune offre trouvée
              </h2>
              <p className="text-sm text-foreground-400 mb-6">
                Aucune offre ne correspond à vos critères. Publiez la vôtre ou élargissez votre recherche.
              </p>
              <Link
                to="/dashboard/jobs/new"
                className="inline-block px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm"
              >
                Publier une offre
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {sorted.map((job) => (
                <article
                  key={job.id}
                  className="rounded-xl bg-background-100 border border-background-200/70 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 hover:border-secondary-400/40 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-secondary-500/10 flex items-center justify-center shrink-0">
                    <i className="ri-briefcase-4-line text-2xl text-secondary-400" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                        {job.category}
                      </span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                        {job.contractType}
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-map-pin-line" />
                        {job.city}
                      </span>
                    </div>
                    <Link to={`/jobs/${job.id}`}>
                      <h2 className="font-heading font-semibold text-foreground-50 leading-snug hover:text-secondary-400 transition-colors">
                        {job.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-foreground-400 mt-1">{job.company}</p>
                  </div>

                  <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
                    <span className="font-semibold text-secondary-400 whitespace-nowrap">
                      {formatSalary(job)}
                    </span>
                    <div className="flex gap-2">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-secondary-500 text-foreground-950 font-medium text-sm hover:bg-secondary-400 transition-colors whitespace-nowrap"
                      >
                        Voir &amp; postuler
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {cities.length > 0 && !loading && !error && sorted.length > 0 && (
            <p className="text-xs text-foreground-400 mt-6 text-center">
              Offres disponibles à : {cities.join(' · ')}
            </p>
          )}
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Vous recrutez ?
            </h2>
            <p className="text-foreground-400 mb-8">
              Publiez votre offre d&apos;emploi et recevez les candidatures directement sur la
              plateforme.
            </p>
            <Link
              to="/dashboard/jobs/new"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line text-lg" />
              Publier une offre d&apos;emploi
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default JobsPage;